/**
 * ENG-6017: shared Claude Code TUI dialog detection + dismissal.
 *
 * Extracted from persistent-session.ts's acceptDialogs() cascade so the
 * dialog knowledge has exactly one home, consumable from:
 *
 *  - `acceptDialogs()` in persistent-session.ts — the spawn-time loop that
 *    walks an agent through the first-run dialog cascade (theme → trust →
 *    MCP → bypass …).
 *  - `channel-input-watchdog.ts` — the per-poll-cycle watchdog that fires
 *    Enter at stuck channel input. Before ENG-6017 the watchdog was
 *    dialog-blind: when Claude Code's session-feedback dialog ("How is
 *    Claude doing this session?") overlaid the pane, the watchdog's
 *    single-shot Enter went into the dialog instead of the input box and
 *    the inbound message sat unsubmitted for 40+ minutes (koda,
 *    2026-06-04) while every health metric stayed green.
 *  - `injectMessageWithStatus()` — pre-send pane hygiene on the tmux
 *    send-keys fallback path.
 *
 * Living in its own module (rather than persistent-session.ts) breaks the
 * import cycle persistent-session → channel-input-watchdog →
 * persistent-session that a shared export would otherwise create.
 *
 * DEFAULT-DENY: `sweepDialogs()` only ever returns an action for an
 * explicitly recognised dialog. An unknown dialog gets `null` — never a
 * blind Enter — because a future dialog's default option could be
 * destructive. Callers that want visibility into unknown overlays should
 * log the pane themselves.
 */

import { execFileSync } from 'node:child_process';

/**
 * A recognised dialog plus the keystrokes that dismiss it.
 *
 * `keys` are tmux key names sent as individual `tmux send-keys` calls —
 * never batched into one call, so a multi-byte sequence can't get wrapped
 * into a single bracketed paste (the CSI-u trap documented on
 * defaultArmSender in persistent-session.ts).
 */
export interface DialogAction {
  kind:
    | 'theme-picker'
    | 'folder-trust'
    | 'resume-mode'
    | 'dev-channels'
    | 'mcp-servers'
    | 'bypass-permissions'
    | 'session-feedback';
  /** tmux key names, one send-keys invocation each. */
  keys: readonly string[];
  /** Delay between consecutive key sends (selector dialogs need a beat
   * between picking an option and confirming it). */
  interKeyDelayMs: number;
  /** Past-tense log fragment, e.g. "Auto-accepted theme picker". Callers
   * append their own context (`for '<codeName>'` etc.). */
  logMessage: string;
}

/**
 * Detect whether Claude Code is showing the **login picker** dialog.
 *
 * ENG-4634: this dialog appears when ~/.claude.json is missing or
 * Claude Code can't validate the saved session. Pressing Enter on
 * the default (1. Claude account with subscription) kicks off a
 * browser-based OAuth flow that an unattended agent can't complete —
 * the helper used to fall through to the generic `❯ no Enter to
 * confirm` exit branch and declare the session "ready" while the
 * actual claude REPL was still on the picker. Without explicit
 * detection, every manager respawn would silently flip the agent
 * back to the picker and never recover.
 *
 * Pattern matches the literal option strings claude renders. Both
 * 'Claude account with subscription' and 'Anthropic Console account'
 * are present on the picker (and not in the post-login UI), so the
 * conjunction is unambiguous.
 *
 * NOT part of sweepDialogs() — the picker must never be keyed past
 * (it needs an operator OAuth); acceptDialogs() handles it specially.
 */
export function isLoginPickerVisible(screen: string): boolean {
  return (
    screen.includes('Select login method') ||
    (screen.includes('Claude account with subscription') &&
      screen.includes('Anthropic Console account'))
  );
}

/**
 * Detect Claude Code's resume-mode dialog (ENG-5364).
 *
 * On `claude --resume <uuid>` against a transcript large enough to
 * trigger Claude Code 2.1.x's context-management heuristic, the agent
 * lands on an interactive picker offering:
 *
 *   ❯ 1. Resume from summary (recommended)
 *     2. Resume full session as-is
 *     3. Don't ask me again
 *
 * Without auto-dismissal the agent sits silently waiting for keyboard
 * input on every manager respawn — channel inbounds stack up while
 * health metrics stay green. Surfaced fleet-wide on 2026-05-20
 * (don/stirling/maven hit it on a single manager restart).
 *
 * Match on the conjunction of two distinct option strings so a
 * passing mention of "Resume" in a transcript doesn't false-positive.
 */
export function isResumeModeDialogVisible(screen: string): boolean {
  return (
    screen.includes('Resume from summary') &&
    screen.includes("Don't ask me again")
  );
}

/**
 * Detect Claude Code's session-feedback dialog (ENG-6017).
 *
 * After some turns Claude Code renders an optional rating prompt:
 *
 *   ● How is Claude doing this session? (optional)
 *     1: Bad    2: Fine   3: Good   0: Dismiss
 *
 * It waits for a digit — Enter does nothing — so any injected message
 * sits in the input box unsubmitted, and the channel-input-watchdog's
 * Enter is swallowed too. Observed live on koda (agt-aws-1) 2026-06-04:
 * an operator Slack DM sat typed-but-unsubmitted for 40+ minutes behind
 * this dialog while pane-activity / synthetic-probe / heartbeat all
 * stayed green.
 *
 * Match the question text together with the literal `0: Dismiss` option
 * so a transcript merely *quoting* the question doesn't false-positive.
 */
export function isSessionFeedbackDialogVisible(screen: string): boolean {
  return (
    screen.includes('How is Claude doing this session') &&
    screen.includes('0: Dismiss')
  );
}

/**
 * Single-pass dialog recognition. Returns the dismissal action for the
 * first recognised dialog on screen, or `null` when no known dialog is
 * visible (including for the login picker, which must never be keyed
 * past — see isLoginPickerVisible).
 *
 * Branch order mirrors the original acceptDialogs() cascade: the theme
 * picker check must run before any generic `❯`-based readiness logic in
 * callers, since picker rows also render with `❯`.
 */
export function sweepDialogs(screen: string): DialogAction | null {
  if (
    screen.includes('Choose the text style') ||
    (screen.includes('Dark mode') && screen.includes('Light mode'))
  ) {
    return {
      kind: 'theme-picker',
      keys: ['Enter'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-accepted theme picker',
    };
  }
  if (screen.includes('Yes, I trust this folder')) {
    return {
      kind: 'folder-trust',
      keys: ['Enter'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-accepted folder trust',
    };
  }
  // ENG-5364: picks option 3 ("Don't ask me again") which Claude Code
  // persists in config, so subsequent resumes skip the dialog entirely.
  if (isResumeModeDialogVisible(screen)) {
    return {
      kind: 'resume-mode',
      keys: ['3', 'Enter'],
      interKeyDelayMs: 300,
      logMessage: "Auto-dismissed resume-mode dialog (picked 'Don't ask me again')",
    };
  }
  if (screen.includes('I am using this for local development')) {
    return {
      kind: 'dev-channels',
      keys: ['Enter'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-accepted dev channels',
    };
  }
  if (screen.includes('Enter to confirm') && screen.includes('MCP')) {
    return {
      kind: 'mcp-servers',
      keys: ['Enter'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-accepted MCP servers',
    };
  }
  if (screen.includes('Yes, I accept') && screen.includes('Bypass Permissions')) {
    return {
      kind: 'bypass-permissions',
      keys: ['2', 'Enter'],
      interKeyDelayMs: 300,
      logMessage: 'Auto-accepted bypass permissions',
    };
  }
  // ENG-6017: the rating prompt acts on the bare digit — no Enter needed.
  if (isSessionFeedbackDialogVisible(screen)) {
    return {
      kind: 'session-feedback',
      keys: ['0'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-dismissed session-feedback dialog',
    };
  }
  return null;
}

/**
 * Send a DialogAction's keystrokes to a tmux session, one send-keys call
 * per key with the action's inter-key delay. execFileSync (not execSync)
 * so the session name is an argv entry rather than shell-interpolated.
 */
export async function sendDialogKeys(
  tmuxSession: string,
  action: DialogAction,
): Promise<void> {
  for (let i = 0; i < action.keys.length; i++) {
    if (i > 0 && action.interKeyDelayMs > 0) {
      await new Promise((r) => setTimeout(r, action.interKeyDelayMs));
    }
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, action.keys[i]!], {
      stdio: 'ignore',
    });
  }
}

/**
 * Tiny non-cryptographic hash for hash-only logging of channel input
 * (prod logging policy: input may contain PII/secrets, so log hash+len,
 * never content). Shared by the watchdog and the inject-time hygiene.
 */
export function simpleTextHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h.toString(16);
}
